import { signalKindSchema } from "@healthos/shared";
import { z } from "zod";
import { calendarDateString } from "./calendar-date.js";
/**
 * The six Logbook pillars. Mirrors `LogbookPillar`
 * (`apps/core-api/src/lib/logbook-detail.ts`) by literal value — declared
 * locally rather than imported, same reasoning that file gives for not
 * importing `packages/ui`'s `LogbookTab`: a schemas package should not
 * acquire a dependency on core-api's internal lib just for a string-literal
 * union.
 */
export const LogbookPillar = z.enum([
    "food",
    "connection",
    "stress",
    "movement",
    "sleep",
    "health",
]);
/**
 * The row-level source split. Matches `LogbookEntry["source"]`
 * (`clients/healthos-web/src/pages/logbook/logbook-entry-card.tsx`) exactly —
 * NOT `LogbookSourceFilter` (`"all" | "mine" | "device"`,
 * `logbook-filter-chips.tsx`), which is a UI-level label over this. A caller
 * sending the "mine" UI label must translate it to "manual" before it
 * reaches this query; this route does not accept "all" as an explicit value
 * because omitting the param already means "all sources".
 */
export const LogbookEntrySource = z.enum(["manual", "device"]);
export const GetLogbookHistoryQuery = z.object({
    pillar: LogbookPillar.optional(),
    source: LogbookEntrySource.optional(),
    /**
     * Narrows further to one exact `SignalKind` within the selected pillar (or
     * across all pillars, if `pillar` is omitted). This is deliberately NOT the
     * same thing as `LogbookFilterChips`' free-text `typeOptions` labels (e.g.
     * "Meals", "Hydration") — there is no server-side mapping from those
     * page-supplied display labels to a `SignalKind` or set of kinds anywhere
     * in this codebase today. A caller wanting to filter by one of those UI
     * labels must resolve it to the matching `SignalKind` itself before
     * sending it here; Tasks 10-12 (not yet built) own that mapping.
     */
    type: signalKindSchema.optional(),
    /**
     * Keyset pagination cursor: an opaque string encoding the previous page's
     * last entry's `occurredAt` AND its own `id` (`encodeLogbookHistoryCursor`,
     * `apps/core-api/src/lib/logbook-history.ts`) — not a plain ISO instant.
     * The compound form is what lets two entries sharing the exact same
     * `occurredAt` (common: Terra writes BP systolic+diastolic, and several
     * wearable kinds, at the identical local-noon stamp) resolve
     * deterministically at a page boundary instead of one of them being
     * silently dropped (final whole-branch review, Finding 3). Pass back the
     * previous page's `nextCursor` verbatim; do not construct one yourself.
     */
    cursor: z.string().optional(),
    limit: z.string().optional(),
    /**
     * Narrows to one local calendar day in the member's zone, bucketed the way
     * the trend chart buckets (a night's sleep lands on the morning it ended).
     * Omitted means every day.
     */
    date: calendarDateString("date").optional(),
});
/**
 * Mirrors `LogbookEntryManualDetails`
 * (`clients/healthos-web/src/pages/logbook/logbook-entry-card.tsx:10-15`) —
 * only the fields that card actually renders.
 */
export const LogbookManualEntryDetails = z.object({
    id: z.string().uuid(),
    editable: z.boolean(),
    detailRows: z.array(z.string()),
});
/**
 * The wire shape of one history row. Mirrors `LogbookEntry`
 * (`clients/healthos-web/src/pages/logbook/logbook-entry-card.tsx:27-38`)
 * field-for-field — this schema IS that reconciled real shape, not a
 * provisional stand-in for it.
 */
export const LogbookHistoryEntry = z.object({
    id: z.string(),
    source: LogbookEntrySource,
    title: z.string(),
    summary: z.string(),
    timeLabel: z.string(),
    occurredAt: z.string(),
    /** YYYY-MM-DD in the member's timezone. */
    localDate: z.string().optional(),
    /** When a device entry's row arrived; null for manual entries. */
    syncedAt: z.string().nullable().optional(),
    sourceLabel: z.string(),
    /** Member-facing "logged from" badge (e.g. "From Slack check-in"), distinct
     * from `sourceLabel`'s device/service name. Mirrors
     * `SignalTimelineItem["sourceBadge"]` (`packages/schemas/src/api/signals.ts`)
     * field-for-field — required-nullable, not optional, because
     * `toLogbookEntry` (`logbook-history.ts`) always sets it unconditionally,
     * same as that type does. `null` when no override applies, so the card
     * falls back to its own default label. */
    sourceBadge: z.string().nullable(),
    manualEntry: LogbookManualEntryDetails.nullable().optional(),
    /**
     * Logbook's duplicate rule (Open Question #2's resolution, recorded in the
     * `logbook-connection` plan's Room history): a manual entry and a
     * device-synced entry describing the same real-world event (same
     * day-bucket + same `SignalKind` — e.g. a hand-logged workout and a
     * wearable-synced one on the same day) both still render as their own
     * history card, rather than merging into one, but trend math counts the
     * pair once, preferring the device reading (`buildLogbookTrend`,
     * `apps/core-api/src/lib/logbook-detail.ts`). This field is the link: the
     * matched device row's raw `signals.id`, set on the MANUAL entry of a
     * matched pair so the card can render a "also synced from <device>"
     * affordance. `null` (or omitted) when no match exists, including always
     * on a device entry itself (a device row never links to another device
     * row under this rule).
     */
    linkedSignalId: z.number().int().positive().nullable().optional(),
    /** The pillar the entry belongs to, so a client can colour it. `null` for a kind no pillar claims. */
    pillar: LogbookPillar.nullable().optional(),
});
export const GetLogbookHistoryResponse = z.object({
    items: z.array(LogbookHistoryEntry),
    hasMore: z.boolean(),
    /** The `cursor` to send for the next page. `null` when `hasMore` is false. */
    nextCursor: z.string().nullable(),
});
/**
 * The one day-range toggle `LogbookTrendChart`/each tab's `days` prop
 * supports. Mirrors `LogbookTrendDays` (`apps/core-api/src/lib/logbook-detail.ts`)
 * by literal value, as a query-string enum rather than that file's numeric
 * union — a query param is always a string on the wire.
 */
export const LogbookTrendDaysParam = z.enum(["7", "14", "28"]);
export const GetLogbookTrendQuery = z.object({
    pillar: LogbookPillar,
    days: LogbookTrendDaysParam,
    /**
     * Overrides "now" for the trend window. Optional — mirrors
     * `GetSignalTimelineQuery["until"]`'s same override, for the same reason:
     * deterministic testing without needing to fake the system clock. Real
     * callers omit it and get the live window.
     */
    until: z.string().datetime().optional(),
    /**
     * Ends the window at the close of this local calendar day in the member's
     * own zone, for a past day's chart. The server resolves the zone, so a
     * phone set to another one cannot shift the day. Wins over `until`.
     */
    date: calendarDateString("date").optional(),
});
export const LogbookTrendPoint = z.object({
    /** Calendar day, UTC, `YYYY-MM-DD`. */
    day: z.string(),
    /** How many of the pillar's signals landed on this day, 0 when none did. */
    count: z.number().int().nonnegative(),
});
/** One day's value for a chart metric, `null` where nothing qualified that
 * day — distinct from `0`, which would plot as a real zero. */
export const LogbookMetricPoint = z.object({
    day: z.string(),
    value: z.number().nullable(),
});
/** One selectable chart series (`apps/core-api/src/lib/logbook-metric-series.ts`'s
 * `buildLogbookMetrics` doc comment has the full per-pillar list). */
export const LogbookMetricOption = z.object({
    key: z.string(),
    label: z.string(),
    unit: z.string().nullable(),
    series: z.array(LogbookMetricPoint),
});
/**
 * The trend route's response: `buildLogbookTrend`'s own three fields
 * (`series`/`sufficientData`/`availableMetrics`, `apps/core-api/src/lib/logbook-detail.ts`)
 * plus every pillar-specific extra stat a tab's own trend bundle needs
 * layered on top, all optional since only one pillar's worth is ever
 * populated per response:
 *  - `averageScore`/`coverage`: Food only, from `buildMealScoreTrend`.
 *  - `checkinCount`: Stress only, from `logbook-trend-extras.ts`.
 *  - `averageSteps`: Movement only, from `logbook-trend-extras.ts`.
 *  - `averageSleepHours`: Sleep only, from `logbook-trend-extras.ts`.
 *  - `entryCount`: Health only — the pillar-wide sum of `series`.
 *  - `connectionCount`: Connection only — the pillar-wide sum of `series`
 *    (see `PILLAR_KINDS.connection`, `connection.log`).
 *  - `mealCount`: Food only, from `logbook-trend-extras.ts` — the
 *    under-the-chart stats row's "meals logged per day" figure.
 *  - `avgConnectionRating`: Connection only, from `logbook-trend-extras.ts`
 *    — the under-the-chart stats row's "average rating" figure.
 */
export const GetLogbookTrendResponse = z.object({
    series: z.array(LogbookTrendPoint),
    sufficientData: z.boolean(),
    availableMetrics: z.array(z.string()),
    /** The chart's real per-day metric(s) — always populated, one entry for
     * a single-metric pillar (Food/Movement/Sleep/Connection), several for
     * Stress/Health. `series` above stays as the pillar-wide entry-frequency
     * count it always was; this is what the chart actually plots now. */
    metrics: z.array(LogbookMetricOption),
    averageScore: z.number().nullable().optional(),
    coverage: z.object({ scored: z.number(), total: z.number() }).optional(),
    checkinCount: z.number().int().nonnegative().optional(),
    averageSteps: z.number().nullable().optional(),
    averageSleepHours: z.number().nullable().optional(),
    entryCount: z.number().int().nonnegative().optional(),
    connectionCount: z.number().int().nonnegative().optional(),
    mealCount: z.number().int().nonnegative().optional(),
    avgConnectionRating: z.number().nullable().optional(),
});
//# sourceMappingURL=logbook.js.map